import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Tag, Space, Modal, Form, Input, Select, Row, Col, message, Empty,
  InputNumber, Switch, DatePicker, Radio, Checkbox, Upload,
} from 'antd';
import { SendOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as workflowService from '../../services/workflowService';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

// 表单模板接口
interface FormTemplate {
  id: string;
  name: string;
  key: string;
  fields: any[];
  status: number;
}

interface ProcessNode {
  id: string;
  name: string;
  type: string;
  formType?: 'template' | 'route';
  formTemplateId?: string;
  formRoute?: string;
  assigneeType?: 'all' | 'user' | 'role';
  assignees?: string[];
}

const WorkflowStart: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [definitions, setDefinitions] = useState<workflowService.WorkflowDefinition[]>([]);
  const [filteredDefinitions, setFilteredDefinitions] = useState<workflowService.WorkflowDefinition[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  
  const [startModalVisible, setStartModalVisible] = useState(false);
  const [selectedDefinition, setSelectedDefinition] = useState<workflowService.WorkflowDefinition | null>(null);
  const [formFields, setFormFields] = useState<any[]>([]);
  
  const [startForm] = Form.useForm();

  // 获取当前用户信息
  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        return JSON.parse(userStr);
      }
    } catch {}
    return null;
  };

  useEffect(() => {
    loadData();
  }, [page]);

  // 过滤当前用户可发起的流程
  useEffect(() => {
    // 如果没有数据，直接设置空数组
    if (definitions.length === 0) {
      setFilteredDefinitions([]);
      return;
    }

    const currentUser = getCurrentUser();
    
    // 如果没有用户信息，显示所有流程（不做权限过滤）
    if (!currentUser) {
      setFilteredDefinitions(definitions);
      return;
    }

    const filtered = definitions.filter(def => {
      try {
        const content = def.content ? JSON.parse(def.content) : null;
        if (!content?.nodes) return true; // 没有节点配置，默认可发起
        
        const startNode = content.nodes.find((n: ProcessNode) => n.type === 'start');
        if (!startNode) return true; // 没有开始节点，默认可发起
        
        const assigneeType = startNode.assigneeType || 'all';
        
        if (assigneeType === 'all') return true; // 所有人可发起
        
        const assignees = startNode.assignees || [];
        if (assignees.length === 0) return true; // 没有配置具体人员，默认可发起
        
        if (assigneeType === 'user') {
          // 指定用户
          return assignees.includes(currentUser.id);
        } else if (assigneeType === 'role') {
          // 指定角色 - 检查roleId是否存在
          if (!currentUser.roleId) return true; // 没有roleId，默认可发起
          return assignees.includes(currentUser.roleId);
        }
        
        return true;
      } catch {
        return true; // 解析失败，默认可发起
      }
    });
    
    setFilteredDefinitions(filtered);
  }, [definitions]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 只加载已发布的流程
      const res = await workflowService.listDefinitions({ page, pageSize: 10, status: 1 });
      if (res.data) {
        setDefinitions(res.data.list || []);
        setTotal(res.data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  // 从localStorage获取所有表单模板
  const getAllFormTemplates = (): FormTemplate[] => {
    try {
      const saved = localStorage.getItem('formTemplates');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {}
    return [];
  };

  const handleStartProcess = (def: workflowService.WorkflowDefinition) => {
    setSelectedDefinition(def);
    startForm.resetFields();
    // 获取开始节点的表单配置
    try {
      const content = def.content ? JSON.parse(def.content) : null;
      if (content?.nodes) {
        const startNode = content.nodes.find((n: ProcessNode) => n.type === 'start');
        // 如果有formTemplateId，就尝试加载表单模板（兼容没有formType字段的情况）
        if (startNode?.formTemplateId) {
          // 直接从localStorage读取最新的表单模板
          const allTemplates = getAllFormTemplates();
          const template = allTemplates.find(t => t.id === startNode.formTemplateId);
          if (template?.fields) {
            setFormFields(template.fields);
          } else {
            setFormFields([]);
          }
        } else if (startNode?.formRoute) {
          // 表单路由类型，暂不处理
          setFormFields([]);
        } else {
          setFormFields([]);
        }
      } else {
        setFormFields([]);
      }
    } catch {
      setFormFields([]);
    }
    setStartModalVisible(true);
  };

  const submitStartProcess = async () => {
    if (!selectedDefinition) return;
    try {
      const values = await startForm.validateFields();
      const formData: Record<string, any> = {};
      formFields.forEach((field: any) => {
        if (values[field.name] !== undefined) formData[field.name] = values[field.name];
      });
      // 保存表单字段配置到variables，用于后续显示label
      const variables: Record<string, any> = {
        _formFields: formFields.map((f: any) => ({
          name: f.name,
          label: f.label,
          type: f.type,
          options: f.options,
          span: f.span,
        })),
      };
      await workflowService.startProcess({
        definitionKey: selectedDefinition.key,
        title: selectedDefinition.name,
        formData,
        variables,
      });
      message.success('流程发起成功');
      setStartModalVisible(false);
    } catch {
      message.error('发起失败');
    }
  };

  const renderFormField = (field: any) => {
    const commonProps = { placeholder: field.placeholder, disabled: field.disabled };
    switch (field.type) {
      case 'input': return <Input {...commonProps} />;
      case 'textarea': return <TextArea {...commonProps} rows={3} />;
      case 'number': return <InputNumber {...commonProps} style={{ width: '100%' }} />;
      case 'select': return <Select {...commonProps}>{field.options?.map((opt: any) => <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>)}</Select>;
      case 'date': return <DatePicker placeholder={field.placeholder} disabled={field.disabled} style={{ width: '100%' }} />;
      case 'dateRange': return <RangePicker disabled={field.disabled} style={{ width: '100%' }} />;
      case 'radio': return <Radio.Group disabled={field.disabled}>{field.options?.map((opt: any) => <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>)}</Radio.Group>;
      case 'checkbox': return <Checkbox.Group disabled={field.disabled} options={field.options?.map((opt: any) => ({ label: opt.label, value: opt.value }))} />;
      case 'switch': return <Switch disabled={field.disabled} />;
      case 'file': return <Upload disabled={field.disabled}><Button icon={<UploadOutlined />}>上传文件</Button></Upload>;
      default: return <Input {...commonProps} />;
    }
  };

  const columns: ColumnsType<workflowService.WorkflowDefinition> = [
    { title: '流程名称', dataIndex: 'name', key: 'name' },
    { title: '流程标识', dataIndex: 'key', key: 'key', render: (key: string) => <Tag>{key}</Tag> },
    { title: '描述', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: '版本', dataIndex: 'version', key: 'version', render: (v: number) => <Tag color="blue">V{v}</Tag> },
    {
      title: '操作', key: 'action', width: 150,
      render: (_, record) => (
        <Space>
          <Button type="primary" icon={<SendOutlined />} onClick={() => handleStartProcess(record)}>发起</Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="发起流程">
        {filteredDefinitions.length === 0 && !loading ? (
          <Empty description="暂无可发起的流程" />
        ) : (
          <Table 
            columns={columns} 
            dataSource={filteredDefinitions} 
            rowKey="id" 
            loading={loading}
            pagination={{ current: page, total: filteredDefinitions.length, pageSize: 10, showTotal: (t) => `共 ${t} 条`, onChange: setPage }} 
          />
        )}
      </Card>

      {/* 发起流程弹窗 */}
      <Modal 
        title={`发起流程 - ${selectedDefinition?.name}`} 
        open={startModalVisible} 
        onCancel={() => setStartModalVisible(false)} 
        onOk={submitStartProcess} 
        width={700}
      >
        <Form form={startForm} layout="vertical">
          {formFields.length > 0 ? (
            <Row gutter={16}>
              {formFields.filter((f: any) => f.visible !== false).map((field: any) => (
                <Col span={field.span || 12} key={field.id}>
                  <Form.Item 
                    name={field.name} 
                    label={field.label} 
                    rules={field.required ? [{ required: true, message: `请输入${field.label}` }] : undefined} 
                    initialValue={field.defaultValue}
                  >
                    {renderFormField(field)}
                  </Form.Item>
                </Col>
              ))}
            </Row>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>该流程暂无表单配置</div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default WorkflowStart;
